<template>
    <Head :title="`Equipment: ${equipment.name}`" />

    <div class="py-6">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <!-- Back Button -->
            <div class="mb-4">
                <Link
                    :href="route('it-equipment.index')"
                    class="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to IT Equipment
                </Link>
            </div>

            <!-- Equipment Header -->
            <div class="bg-white rounded-lg shadow overflow-hidden mb-6">
                <div class="p-6">
                    <div class="flex flex-col md:flex-row gap-6">
                        <!-- Equipment Info -->
                        <div class="flex-1">
                            <div class="flex items-start justify-between">
                                <div>
                                    <p class="text-sm text-gray-500">{{ equipment.equipment_number }}</p>
                                    <div class="flex items-center gap-3 mt-1">
                                        <h1 class="text-3xl font-bold text-gray-900">{{ equipment.name }}</h1>
                                        <InfoButton title="Equipment Details & Management" variant="default">
                                            <p>
                                                <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                                                The Equipment Details page provides a complete view of a single IT equipment's information
                                                and user assignments. This centralized view helps you track equipment usage, assignments,
                                                and specifications all in one place.
                                            </p>
                                            <p>
                                                <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                                                • <strong>Quick Actions:</strong> Edit equipment details or assign users to equipment<br>
                                                • <strong>Basic Information:</strong> View equipment name, number, category, location, status, and condition<br>
                                                • <strong>Equipment Details:</strong> View manufacturer, model, serial number, and barcode<br>
                                                • <strong>Financial Information:</strong> Track purchase date, cost, and warranty expiration<br>
                                                • <strong>Equipment Specification:</strong> View custom specifications and technical details<br>
                                                • <strong>User Assignments:</strong> Manage which users have access to this equipment with roles and notes
                                            </p>
                                        </InfoButton>
                                    </div>
                                    <div class="mt-2 flex items-center space-x-3">
                                        <span
                                            :class="{
                                                'bg-green-100 text-green-800': equipment.status === 'in_use',
                                                'bg-blue-100 text-blue-800': equipment.status === 'available',
                                                'bg-yellow-100 text-yellow-800': equipment.status === 'under_repair',
                                                'bg-gray-100 text-gray-800': equipment.status === 'retired',
                                            }"
                                            class="px-3 py-1 text-sm font-medium rounded-full"
                                        >
                                            {{ equipment.status.replace('_', ' ').toUpperCase() }}
                                        </span>
                                        <span v-if="equipment.condition" class="px-3 py-1 text-sm font-medium rounded-full bg-gray-100 text-gray-800">
                                            {{ equipment.condition.toUpperCase() }} Condition
                                        </span>
                                    </div>
                                </div>

                                <!-- Action Buttons -->
                                <div class="flex items-center space-x-2">
                                    <Link
                                        v-if="canEdit"
                                        :href="route('it-equipment.edit', equipment.id)"
                                        class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                    >
                                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                        Edit
                                    </Link>
                                    <button
                                        v-if="canAssign"
                                        @click="showAssignUserModal = true"
                                        class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                    >
                                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                        </svg>
                                        Assign User
                                    </button>
                                </div>
                            </div>

                            <!-- Quick Info Grid -->
                            <div class="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div>
                                    <p class="text-xs text-gray-500">Category</p>
                                    <p class="mt-1 text-sm font-medium text-gray-900">{{ equipment.category?.name || '-' }}</p>
                                </div>
                                <div>
                                    <p class="text-xs text-gray-500">Location</p>
                                    <p class="mt-1 text-sm font-medium text-gray-900">{{ equipment.location?.path_with_type || '-' }}</p>
                                </div>
                                <div>
                                    <p class="text-xs text-gray-500">Primary User</p>
                                    <p class="mt-1 text-sm font-medium text-gray-900">{{ equipment.primary_user?.name || '-' }}</p>
                                </div>
                                <div>
                                    <p class="text-xs text-gray-500">Total Users</p>
                                    <p class="mt-1 text-sm font-medium text-gray-900">{{ equipment.users?.length || 0 }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Equipment Details Content -->
            <div class="bg-white rounded-lg shadow p-6">
                <div class="space-y-8">
                    <!-- Basic Information -->
                    <div>
                        <h3 class="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                        <dl class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
                            <div>
                                <dt class="text-sm font-medium text-gray-500">Equipment Name</dt>
                                <dd class="mt-1 text-sm text-gray-900">{{ equipment.name }}</dd>
                            </div>
                            <div>
                                <dt class="text-sm font-medium text-gray-500">Equipment Number</dt>
                                <dd class="mt-1 text-sm text-gray-900">{{ equipment.equipment_number }}</dd>
                            </div>
                            <div>
                                <dt class="text-sm font-medium text-gray-500">Category</dt>
                                <dd class="mt-1 text-sm text-gray-900">{{ equipment.category?.name || '-' }}</dd>
                            </div>
                            <div>
                                <dt class="text-sm font-medium text-gray-500">Location</dt>
                                <dd class="mt-1 text-sm text-gray-900">{{ equipment.location?.path_with_type || '-' }}</dd>
                            </div>
                            <div>
                                <dt class="text-sm font-medium text-gray-500">Status</dt>
                                <dd class="mt-1 text-sm text-gray-900">{{ equipment.status.replace('_', ' ').toUpperCase() }}</dd>
                            </div>
                            <div>
                                <dt class="text-sm font-medium text-gray-500">Condition</dt>
                                <dd class="mt-1 text-sm text-gray-900">{{ equipment.condition?.toUpperCase() || '-' }}</dd>
                            </div>
                        </dl>
                    </div>

                    <!-- Equipment Details -->
                    <div>
                        <h3 class="text-lg font-medium text-gray-900 mb-4">Equipment Details</h3>
                        <dl class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
                            <div>
                                <dt class="text-sm font-medium text-gray-500">Manufacturer</dt>
                                <dd class="mt-1 text-sm text-gray-900">{{ equipment.manufacturer || '-' }}</dd>
                            </div>
                            <div>
                                <dt class="text-sm font-medium text-gray-500">Model</dt>
                                <dd class="mt-1 text-sm text-gray-900">{{ equipment.model || '-' }}</dd>
                            </div>
                            <div>
                                <dt class="text-sm font-medium text-gray-500">Serial Number</dt>
                                <dd class="mt-1 text-sm text-gray-900">{{ equipment.serial_number || '-' }}</dd>
                            </div>
                            <div>
                                <dt class="text-sm font-medium text-gray-500">Barcode</dt>
                                <dd class="mt-1 text-sm text-gray-900">{{ equipment.barcode || '-' }}</dd>
                            </div>
                        </dl>
                    </div>

                    <!-- Financial Information -->
                    <div>
                        <h3 class="text-lg font-medium text-gray-900 mb-4">Financial Information</h3>
                        <dl class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
                            <div>
                                <dt class="text-sm font-medium text-gray-500">Purchase Date</dt>
                                <dd class="mt-1 text-sm text-gray-900">{{ formatDate(equipment.purchase_date) }}</dd>
                            </div>
                            <div>
                                <dt class="text-sm font-medium text-gray-500">Purchase Cost</dt>
                                <dd class="mt-1 text-sm text-gray-900">{{ equipment.purchase_cost ? formatMoney(equipment.purchase_cost) : '-' }}</dd>
                            </div>
                            <div>
                                <dt class="text-sm font-medium text-gray-500">Warranty Expiry</dt>
                                <dd class="mt-1 text-sm text-gray-900">{{ formatDate(equipment.warranty_expiry_date) }}</dd>
                            </div>
                        </dl>
                    </div>

                    <!-- Equipment Specification -->
                    <div v-if="equipment.custom_fields && Object.keys(equipment.custom_fields).length > 0">
                        <h3 class="text-lg font-medium text-gray-900 mb-4">Equipment Specification</h3>
                        <dl class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
                            <div v-for="(value, key) in equipment.custom_fields" :key="key">
                                <dt class="text-sm font-medium text-gray-500">{{ key }}</dt>
                                <dd class="mt-1 text-sm text-gray-900">{{ value }}</dd>
                            </div>
                        </dl>
                    </div>

                    <!-- Notes -->
                    <div v-if="equipment.notes">
                        <h3 class="text-lg font-medium text-gray-900 mb-4">Notes</h3>
                        <p class="text-sm text-gray-900 whitespace-pre-wrap">{{ equipment.notes }}</p>
                    </div>

                    <!-- User Assignments -->
                    <div>
                        <div class="flex items-center justify-between mb-4">
                            <h3 class="text-lg font-medium text-gray-900">User Assignments</h3>
                            <button
                                v-if="canAssign"
                                @click="showAssignUserModal = true"
                                class="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                Assign User
                            </button>
                        </div>

                        <div v-if="equipment.users && equipment.users.length > 0">
                            <div class="space-y-3">
                                <div
                                    v-for="user in equipment.users"
                                    :key="user.id"
                                    class="flex items-center justify-between p-3 border border-gray-200 rounded-md"
                                >
                                    <div class="flex items-center space-x-3">
                                        <div class="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                                            <svg class="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p class="text-sm font-medium text-gray-900">{{ user.name }}</p>
                                            <p class="text-xs text-gray-500">{{ user.email }}</p>
                                            <div class="flex items-center mt-1 space-x-2">
                                                <span
                                                    :class="{
                                                        'bg-blue-100 text-blue-800': user.pivot.role === 'primary_user',
                                                        'bg-gray-100 text-gray-800': user.pivot.role === 'secondary_user',
                                                        'bg-purple-100 text-purple-800': user.pivot.role === 'admin',
                                                    }"
                                                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                                >
                                                    {{ user.pivot.role.replace('_', ' ').toUpperCase() }}
                                                </span>
                                                <span class="text-xs text-gray-500">
                                                    Assigned {{ formatDate(user.pivot.assigned_at) }}
                                                </span>
                                            </div>
                                            <p v-if="user.pivot.notes" class="text-xs text-gray-500 mt-1">{{ user.pivot.notes }}</p>
                                        </div>
                                    </div>
                                    <button
                                        v-if="canAssign"
                                        @click="removeUser(user.id)"
                                        class="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Empty State -->
                        <div v-else class="text-center py-6">
                            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                            <p class="mt-2 text-sm text-gray-500">No users have been assigned to this equipment</p>
                            <button
                                v-if="canAssign"
                                @click="showAssignUserModal = true"
                                class="mt-3 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                            >
                                Assign First User
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Assign User Modal (placeholder for now) -->
    <!-- This would need to be implemented with a proper modal component -->
</template>

<script setup>
import { ref } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';
import { useToast } from '@/Composables/useToast';
import { useCurrency } from '@/Composables/useCurrency';

const props = defineProps({
    equipment: Object,
    canEdit: {
        type: Boolean,
        required: true,
    },
    canDelete: {
        type: Boolean,
        required: true,
    },
    canAssign: {
        type: Boolean,
        required: true,
    },
});

const toast = useToast();
const { formatMoney } = useCurrency();
const showAssignUserModal = ref(false);

// Format dates to show only date part (no time)
const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const removeUser = (userId) => {
    if (!confirm('Are you sure you want to remove this user assignment?')) {
        return;
    }

    router.post(route('it-equipment.remove-user', props.equipment.id), {
        user_id: userId,
    }, {
        onSuccess: () => {
            toast.success('User removed successfully!');
        },
        onError: () => {
            toast.error('Failed to remove user.');
        },
    });
};
</script>
